import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { BotSubNav } from './BotSubNav';
import { BOT_STATE_LABELS_HE, SERVICE_LABELS_HE } from './bot.schema';
import { useBotAnalytics } from './botQueries';
import styles from './bot.module.css';

function pct(n: number): string {
  return `${String(Math.round(n * 100))}%`;
}

export function BotAnalyticsPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const { data: k, isLoading } = useBotAnalytics(orgId);

  const maxStateCount = k ? Math.max(1, ...k.stateFunnel.map((s) => s.count)) : 1;
  const maxServiceCount = k ? Math.max(1, ...k.serviceMix.map((s) => s.count)) : 1;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>אנליטיקה של הבוט</h1>
          <p className={styles.subtitle}>מסילת שלבים, אחוזי מעבר, השירותים המבוקשים.</p>
        </div>
      </header>

      <BotSubNav />

      {isLoading || !k ? (
        <p className={styles.notice}>טוען נתונים...</p>
      ) : (
        <>
          <section className={styles.kpiStrip}>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>שיחות חדשות (7 ימים)</span>
              <span className={styles.kpiValue}>
                <span className="num">{String(k.newConvs7d)}</span>
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>שיעור מסירה</span>
              <span className={styles.kpiValue}>
                <span className="num">{pct(k.handoffRate)}</span>
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>שיעור fallback</span>
              <span className={styles.kpiValue}>
                <span className="num">{pct(k.fallbackRate)}</span>
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>ממוצע הודעות לשיחה</span>
              <span className={styles.kpiValue}>
                <span className="num">{String(k.avgMessagesPerConv)}</span>
              </span>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>מסילת השלבים</h2>
            <div className={styles.funnelBars}>
              {k.stateFunnel.map((s) => (
                <div key={s.state} className={styles.funnelRow}>
                  <span className={styles.funnelLabel}>{BOT_STATE_LABELS_HE[s.state]}</span>
                  <div className={styles.funnelBar}>
                    <div
                      className={styles.funnelFill}
                      style={{ width: `${String((s.count / maxStateCount) * 100)}%` }}
                    />
                  </div>
                  <span className={styles.funnelCount}>
                    <span className="num">{String(s.count)}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>תמהיל שירותים</h2>
            {k.serviceMix.length === 0 ? (
              <p className={styles.notice}>אין עדיין נתונים.</p>
            ) : (
              <div className={styles.funnelBars}>
                {k.serviceMix.map((s) => (
                  <div key={s.slug ?? 'none'} className={styles.funnelRow}>
                    <span className={styles.funnelLabel}>
                      {s.slug ? SERVICE_LABELS_HE[s.slug] : 'לא הוגדר'}
                    </span>
                    <div className={styles.funnelBar}>
                      <div
                        className={styles.funnelFill}
                        style={{ width: `${String((s.count / maxServiceCount) * 100)}%` }}
                      />
                    </div>
                    <span className={styles.funnelCount}>
                      <span className="num">{String(s.count)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
